import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { get, isNumber } from 'lodash'
import { CustomFields } from 'components'
import {
  Grid,
  TextField,
  NumberField,
  Section,
  FieldBlock,
  ButtonBlock,
  Button,
} from '@politechdev/blocks-design-system'
import { useForm } from 'contexts'

const ShiftForm = ({ shift, saveForm }) => {
  const { t } = useTranslation()

  const { formData, setField, setFormData } = useForm()

  const customFieldConfig = shift.turf.options.custom_field_configs?.find(
    c => c.table === 'shifts'
  )

  const customFieldsDefaults = customFieldConfig
    ? customFieldConfig.fields.reduce((defaults, field) => {
        if (field.type === 'integer') {
          defaults[field.name] = 0
        }

        return defaults
      }, {})
    : {}

  useEffect(() => {
    setFormData(formData => ({
      ...formData,
      soft_count_cards_total_collected: shift.soft_count_cards_total_collected,
      soft_count_cards_complete_collected:
        shift.soft_count_cards_complete_collected,
      soft_count_cards_incomplete_collected:
        shift.soft_count_cards_incomplete_collected,
      soft_count_cards_with_phone_collected:
        shift.soft_count_cards_with_phone_collected,
      soft_count_pre_registration_cards_collected:
        shift.soft_count_cards_with_phone_collected,
      notes: shift.notes,
      custom_field_data: shift.custom_field_data || customFieldsDefaults,
    }))
  }, [])

  const setCustomField = (field, val) =>
    setField(
      { ...formData.custom_field_data, [field]: val },
      'custom_field_data'
    )

  const areCountsConflicting =
    isNumber(formData.soft_count_cards_total_collected) &&
    isNumber(formData.soft_count_cards_complete_collected) &&
    isNumber(formData.soft_count_cards_incomplete_collected) &&
    formData.soft_count_cards_total_collected !==
      formData.soft_count_cards_complete_collected +
        formData.soft_count_cards_incomplete_collected

  const arePhoneFormsMoreThanTotalForms =
    isNumber(formData.soft_count_cards_total_collected) &&
    isNumber(formData.soft_count_cards_with_phone_collected) &&
    formData.soft_count_cards_with_phone_collected >
      formData.soft_count_cards_total_collected

  const isStaticFormValid =
    isNumber(formData.soft_count_cards_with_phone_collected) &&
    isNumber(formData.soft_count_pre_registration_cards_collected) &&
    !areCountsConflicting &&
    !arePhoneFormsMoreThanTotalForms

  const isCustomFormValid =
    !customFieldConfig ||
    !customFieldConfig.fields.some(field => {
      const value = get(formData.custom_field_data, field.name)
      const isInvalid = !value && value !== 0
      return field.required && isInvalid
    })

  const isFormValid = isStaticFormValid && isCustomFormValid

  return (
    <Grid>
      <Section>
        <form>
          <FieldBlock>
            <NumberField
              id="cards_collected"
              required
              min={0}
              label={t('Registration forms collected')}
              value={formData.soft_count_cards_total_collected}
              onChange={val =>
                setField(+val, 'soft_count_cards_total_collected')
              }
              error={areCountsConflicting}
              errorMessage={t(
                'Does not equal the complete and incomplete registration form collected fields'
              )}
            />
            <NumberField
              id="cards_complete"
              required
              label={t('Registration forms complete')}
              min={0}
              value={formData.soft_count_cards_complete_collected}
              onChange={val =>
                setField(+val, 'soft_count_cards_complete_collected')
              }
            />
            <NumberField
              id="cards_incomplete"
              required
              label={t('Registration forms incomplete')}
              min={0}
              value={formData.soft_count_cards_incomplete_collected}
              onChange={val =>
                setField(+val, 'soft_count_cards_incomplete_collected')
              }
            />
            <NumberField
              id="cards_with_phone"
              required
              label={t('Registration forms with phone')}
              min={0}
              error={arePhoneFormsMoreThanTotalForms}
              errorMessage={t(
                'Cannot have more registration forms with phone than total collected registration forms'
              )}
              value={formData.soft_count_cards_with_phone_collected}
              onChange={val =>
                setField(+val, 'soft_count_cards_with_phone_collected')
              }
            />
            <NumberField
              id="pre_reg_cards"
              required
              label={t('Pre-reg forms')}
              min={0}
              value={formData.soft_count_pre_registration_cards_collected}
              onChange={val =>
                setField(+val, 'soft_count_pre_registration_cards_collected')
              }
            />
          </FieldBlock>
          {!!customFieldConfig && (
            <FieldBlock>
              <CustomFields
                fields={customFieldConfig.fields}
                currentData={formData.custom_field_data || {}}
                onChange={setCustomField}
              />
            </FieldBlock>
          )}
          <FieldBlock variant="large">
            <TextField
              id="notes"
              label={t('Notes')}
              value={formData.notes || ''}
              onChange={val => setField(val, 'notes')}
              type="text"
            />
          </FieldBlock>
          <ButtonBlock justify="left">
            <Button.Accent
              disabled={!isFormValid}
              onClick={() => saveForm(formData)}
            >
              {t('Save')}
            </Button.Accent>
          </ButtonBlock>
        </form>
      </Section>
    </Grid>
  )
}

export default ShiftForm
